(ns io.daskul.tutorial.webapp.router
  (:require
   [reitit.frontend :as rf]
   [reitit.frontend.easy :as rfe]
   [re-frame.core :as re-frame]
   [io.daskul.webapp.routes :as routes]))

;; Routes:
;; /:school-code/login             - Login page (public)
;; /:school-code/                  - Home/Today (protected)
;; /:school-code/analytics         - Analytics (dashboard sections, admins)
;; Aggregate routes come from io.daskul.webapp.routes/aggregate-routes, shared
;; with daskul-basiced. Their ORDER is load-bearing and the router needs
;; `routes/router-opts` — see that namespace before touching either.

;; School code extracted from URL on first navigation, stored here for auto-injection.
(defonce ^:private school-code (atom nil))

(defn get-school-code [] @school-code)

(def routes
  [""
   ["/"
    {:name :root
     :public? true}]

   ["/:school-code"
    [""
     {:name :school-root
      :public? true}]

   ["/login"
    {:name :login
     :public? true}]

   ["/"
    {:name :home}]

   routes/aggregate-routes

   ["/inbox"
    {:name :inbox}]

   ["/conversations/:id"
    {:name :conversation/thread}]

   ["/calendar"
    {:name :calendar}]

   ["/change-password"
    {:name :change-password}]

   ["/settings"
    {:name :settings}]

   ["/analytics"
    {:name :analytics}]]])

;; `routes/router-opts` disables reitit's conflict detection. It is REQUIRED:
;; /a/:agg/:id genuinely conflicts with /a/:agg/new and /a/:agg/edit/:id, and
;; reitit throws at construction rather than mis-routing — omitting this is a
;; startup crash, not a subtle bug.
(def router
  (rf/router routes routes/router-opts))

;; ============================================================================
;; Navigation state (re-frame)
;; ============================================================================

(re-frame/reg-event-fx
 ::navigated
 (fn [{:keys [db]} [_ match]]
   (let [code (get-in match [:path-params :school-code])
         first-load? (and code (nil? @school-code))]
     (when code (reset! school-code code))
     (cond-> {:db (assoc db :current-route match)}
       ;; Fetch school info on first navigation
       first-load?
       (assoc :dispatch [:io.daskul.tutorial.webapp.app.events/fetch-school-info code])))))

(re-frame/reg-sub
 ::current-route
 (fn [db]
   (:current-route db)))

(re-frame/reg-sub
 ::route-name
 :<- [::current-route]
 (fn [route]
   (get-in route [:data :name])))

(re-frame/reg-sub
 ::route-params
 :<- [::current-route]
 (fn [route]
   (get-in route [:path-params])))

(re-frame/reg-sub
 ::school-code
 :<- [::current-route]
 (fn [route]
   (get-in route [:path-params :school-code])))

;; ============================================================================
;; Router initialization
;; ============================================================================

(defn on-navigate [match]
  (when match
    (if (routes/legacy-edit-match? match)
      ;; /a/:agg/edit/:id retired (F-060). REPLACE rather than push: a redirect
      ;; that pushes leaves the retired URL in history and Back bounces off it.
      ;; Not counted as a navigation — it is a rewrite of where we already are.
      (let [[route-name path-params query-params] (routes/legacy-edit-redirect match)]
        (rfe/replace-state route-name path-params query-params))
      (do
        (routes/note-navigation!)
        (re-frame/dispatch [::navigated match])))))

(defn init! []
  (rfe/start!
   router
   on-navigate
   {:use-fragment false}))

;; ============================================================================
;; Navigation helpers (auto-inject :school-code)
;; ============================================================================

(defn- inject-school-code [params]
  (if (:school-code params)
    params
    (assoc params :school-code @school-code)))

(defn href
  "Generate href for a route. School code is injected automatically."
  [route-name & [params query-params]]
  (rfe/href route-name (inject-school-code (or params {})) query-params))

(defn navigate!
  "Navigate to a route. School code is injected automatically."
  [route-name & [params query-params]]
  (rfe/push-state route-name (inject-school-code (or params {})) query-params))

(defn back!
  "The in-page Back link. `history.back` when there is somewhere in-app to go
   — it returns to the previous tab (tabs are real history entries now) or to
   the list with its filters and scroll intact — else a real navigation,
   because a deep link has nothing behind it and Back would leave the app."
  [match]
  (if (routes/can-go-back?)
    (js/window.history.back)
    (let [[route-name path-params] (routes/back-fallback-target match)]
      (navigate! route-name path-params))))

(defn push-query-params!
  "Navigate with query params (creates history entry — back/forward works)."
  [route-name path-params query-params]
  (rfe/push-state route-name (inject-school-code (or path-params {})) query-params))

(defn replace-query-params!
  "Update query params without creating a history entry (for typing/debounce)."
  [route-name path-params query-params]
  (rfe/replace-state route-name (inject-school-code (or path-params {})) query-params))

;; ============================================================================
;; Navigation event (for dispatching from re-frame events)
;; ============================================================================

(re-frame/reg-event-fx
 ::navigate
 (fn [_ [_ route-name params]]
   (navigate! route-name params)
   {}))
